/**
 * Event log core: append, read, dedupe-by-eventId, canonical ordering.
 *
 * Canonical order of the log is lexicographic `eventId` (UUIDv7 embeds
 * client time, so string order = creation order) -- ADR-0002 "Ordering and
 * last-write-wins". This module is a pure, in-memory representation of "the
 * log so far"; persistence lives in `src/data` behind `EventCachePort`
 * (ADR-0001 rule 2: no I/O in `src/domain`).
 */

import type { CompassEvent, EventId } from '../../contracts';

/**
 * Sort events into canonical log order (ascending lexicographic `eventId`).
 * Pure, stable, does not mutate the input.
 */
export function sortByEventId(
  events: readonly CompassEvent[],
): readonly CompassEvent[] {
  return [...events].sort((a, b) =>
    a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0,
  );
}

/**
 * Merge one or more event batches into a single deduped, canonically ordered
 * log. Dedupe key is `eventId` (ADR-0002); when the "same" ID appears more
 * than once (should never happen in practice -- IDs are unique by
 * construction -- but a poison/impostor payload is a documented threat model
 * in ADR-0005), the FIRST occurrence in argument order wins, matching the
 * server's first-write-wins rule.
 */
export function mergeEvents(
  ...batches: ReadonlyArray<readonly CompassEvent[]>
): readonly CompassEvent[] {
  const byId = new Map<EventId, CompassEvent>();
  for (const batch of batches) {
    for (const event of batch) {
      if (!byId.has(event.eventId)) {
        byId.set(event.eventId, event);
      }
    }
  }
  return sortByEventId([...byId.values()]);
}

/**
 * Deduplicate a single list of events by `eventId`, keeping the first
 * occurrence and preserving canonical order.
 */
export function dedupeEvents(
  events: readonly CompassEvent[],
): readonly CompassEvent[] {
  return mergeEvents(events);
}

/**
 * Narrow a log to events of one or more discriminant types, preserving
 * canonical order. A small convenience used throughout the projection layer
 * (objectives/actions/forecast) to avoid repeating `.filter` predicates.
 */
export function eventsOfTypes<T extends CompassEvent['type']>(
  events: readonly CompassEvent[],
  types: readonly T[],
): ReadonlyArray<Extract<CompassEvent, { readonly type: T }>> {
  const typeSet = new Set<string>(types);
  const filtered = events.filter((event) => typeSet.has(event.type));
  // The filter predicate narrows by `type` at runtime, but its shape (a
  // Set membership check) is not something the type checker can turn into a
  // type guard on its own -- the cast documents that the narrowing is
  // correct by construction (every event's `type` matches its own variant).
  return filtered as unknown as ReadonlyArray<
    Extract<CompassEvent, { readonly type: T }>
  >;
}

/**
 * The set of `eventId`s that have been targeted by a `log_entry.reverted`
 * event (ADR-0002). Valid targets are documented at the type level, not
 * enforced here -- this function is agnostic to what kind of event was
 * reverted, matching the generic revert primitive's design.
 */
export function revertedEventIds(
  events: readonly CompassEvent[],
): ReadonlySet<EventId> {
  const reverted = new Set<EventId>();
  for (const event of events) {
    if (event.type === 'log_entry.reverted') {
      reverted.add(event.revertedEventId);
    }
  }
  return reverted;
}

/**
 * Filter out events that have been reverted (ADR-0002: derivation treats a
 * reverted target as absent). The `log_entry.reverted` events themselves are
 * NOT filtered out by this function -- they stay in the log; callers that
 * only care about "active" domain events typically also exclude
 * `log_entry.reverted` from the result via `eventsOfTypes` upstream.
 */
export function excludingReverted(
  events: readonly CompassEvent[],
): readonly CompassEvent[] {
  const reverted = revertedEventIds(events);
  return events.filter((event) => !reverted.has(event.eventId));
}
